/**
 * The catalogue in force (2026-10-09) — `GET /billing/catalogue`, which lays
 * a platform admin's prices and tier allowances over the code's. Every screen
 * that names a price reads it here, never `PRODUCTS[…].priceCents`. Until it
 * answers — or with no signal — the code's own catalogue stands in.
 *
 * The cache holds the endpoint's own shape (one key, one shape); the derived
 * `Catalogue` is each caller's `select`.
 */
import { useQuery } from '@tanstack/react-query';
import { resolveCatalogue, type Catalogue, type ProductId, type Tier } from '@myrave/shared';
import { api } from './api';

export interface CatalogueAnswer {
  products: { id: ProductId; priceCents: number; kind: 'one-time' | 'yearly' }[];
  tiers: { tier: Tier; ravesPerYear: number | null; freePhotosPerYear: number | null; unlimitedLikes: boolean }[];
  /**
   * True only while the server takes money. False with `PURCHASES_DISABLED`
   * (every rave open) OR the deployment's `PAYMENTS_PAUSED` (the kill switch,
   * paid raves stay paid) — and then NO payment shows anywhere (the user,
   * 2026-10-29). The one answer every screen asks, through `usePurchasesOn`.
   */
  purchasesOn?: boolean;
  /** A donation is not a purchase (step 108): on while purchases are off, off while payments are paused. */
  donationsOn?: boolean;
  /** The kill switch is on (environment only since 2026-10-29). Older servers send none. */
  paymentsPaused?: boolean;
}

export const CATALOGUE_KEY = ['billing', 'catalogue'] as const;

export function fromAnswer(a: CatalogueAnswer | undefined): Catalogue {
  if (!a?.products || !a.tiers) return resolveCatalogue(null);
  return resolveCatalogue({
    prices: Object.fromEntries(a.products.map((p) => [p.id, p.priceCents])),
    tiers: Object.fromEntries(a.tiers.map((t) => [t.tier, { ravesPerYear: t.ravesPerYear, freePhotosPerYear: t.freePhotosPerYear }])),
  });
}

/** The catalogue in force; the code's while the answer is on its way. */
export function useCatalogue(): Catalogue {
  const q = useQuery({
    queryKey: CATALOGUE_KEY,
    queryFn: () => api.get<CatalogueAnswer>('/billing/catalogue'),
    staleTime: 5 * 60_000,
  });
  return fromAnswer(q.data);
}

/**
 * The catalogue as the API answered it — `null` until it has (the public site,
 * 2026-10-14). A page that shows prices to somebody deciding whether to sign
 * up shows the prices in force or none: the code's own values are a fallback
 * for screens that must work offline, not prices to advertise.
 */
export function useAnsweredCatalogue(): Catalogue | null {
  const q = useQuery({
    queryKey: CATALOGUE_KEY,
    queryFn: () => api.get<CatalogueAnswer>('/billing/catalogue'),
    staleTime: 5 * 60_000,
  });
  // Purchases off: there are no prices to show (the user, 2026-10-21).
  return q.data?.products && q.data.tiers && q.data.purchasesOn === true ? fromAnswer(q.data) : null;
}

/**
 * Can anything be bought here? `null` until the API has said; `false` when it
 * cannot be asked (an error). While payments are off — `PURCHASES_DISABLED`
 * (the user, 2026-10-21: "all the system is free until I decide to activate
 * the purchases") or `PAYMENTS_PAUSED` (2026-10-29: "all the payment stuff on
 * all the pages should be hidden, including settings, support, everywhere")
 * — every payment element is hidden: Plans in every menu and `/plans`, prices
 * and passes, the paywall (`PaidRave`), photo buying, the account's Plans,
 * Payments and Restore purchases, the notification topic, the Support
 * Center's payment topic, the console's Payments group and the rave admin's
 * pricing and coupons. So a screen shows any of it only on `true`, never on a
 * guess — this hook is the ONE place it is decided.
 */
export function usePurchasesOn(): boolean | null {
  const q = useQuery({
    queryKey: CATALOGUE_KEY,
    queryFn: () => api.get<CatalogueAnswer>('/billing/catalogue'),
    staleTime: 5 * 60_000,
  });
  // Only an explicit yes: an answer without the field (an older API, or one
  // cached from before it) shows no Plans (the user saw it on dev, 2026-10-21).
  if (q.data) return q.data.purchasesOn === true;
  return q.isError ? false : null;
}

/**
 * Can a donation be made here (step 108)? Not a purchase — the user, 2026-11-02: "purchase is disabled — donate is not
 * purchase" — so it does not follow `usePurchasesOn`: on unless payments are paused. `null` until the API has said.
 */
export function useDonationsOn(): boolean | null {
  const q = useQuery({
    queryKey: CATALOGUE_KEY,
    queryFn: () => api.get<CatalogueAnswer>('/billing/catalogue'),
    staleTime: 5 * 60_000,
  });
  if (q.data) return q.data.donationsOn === true;
  return q.isError ? false : null;
}
